import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { resizeImage, uploadToBucket, deleteFromBucket } from '../lib/storage'
import type { ProductPhoto } from '../lib/database.types'

const PHOTOS_KEY = (productId: string) => ['product_photos', productId]

export function useProductPhotos(productId: string | undefined) {
  return useQuery({
    queryKey: PHOTOS_KEY(productId ?? ''),
    enabled: !!productId,
    queryFn: async (): Promise<ProductPhoto[]> => {
      const { data, error } = await supabase
        .from('product_photos')
        .select('*')
        .eq('product_id', productId!)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUploadProductPhoto(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, isCover }: { file: File; isCover?: boolean }): Promise<ProductPhoto> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const blob = await resizeImage(file)
      const path = await uploadToBucket('photos', productId, file.name, blob)

      // If marking as cover, demote any existing cover first. Throw on
      // failure — silently swallowing would leave two photos flagged as
      // cover and the UI would pick one at random.
      if (isCover) {
        const { error: demoteErr } = await supabase
          .from('product_photos')
          .update({ is_cover: false })
          .eq('product_id', productId)
          .eq('is_cover', true)
        if (demoteErr) throw demoteErr
      }

      const { data, error } = await supabase
        .from('product_photos')
        .insert({
          user_id: user.id,
          product_id: productId,
          file_path: path,
          is_cover: isCover ?? false,
        })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHOTOS_KEY(productId) })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useDeleteProductPhoto(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (photo: ProductPhoto): Promise<void> => {
      await deleteFromBucket('photos', photo.file_path)
      const { error } = await supabase.from('product_photos').delete().eq('id', photo.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PHOTOS_KEY(productId) })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}
